import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { TabsInnerWrapComponent } from '../../components/tabs-inner-wrap/tabs-inner-wrap.component';
import { DetailsSectionComponent } from '../../components/details-container/details-section.component';
import { KebabMenuItem, QaiButtonComponent, QaiFieldComponent, QaiInputComponent, QaiLoadingComponent, QaiMultiSelectComponent, QaiMultiSelectOption, QaiTableComponent, SortDirection, TableRow, QaiIconComponent, QaiSlotDirective } from '@qai/ui';
import { ActivatedRoute } from '@angular/router';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { catchError, Observable, of, tap, map } from 'rxjs';
import { AddUserToAssignmentModalComponent } from '../../components/modals/add-users-to-assignment/add-users-to-assignment-modal.component';
import { IParsedTeam } from 'src/app/interfaces/self-serve/assignment-manager/iteams-with-users-parser';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

export const ASSIGNEES_TABLE_HEADERS = ['Name', 'Team', 'Excluded from Reporting'];

@Component({
  selector: 'tab-assigness-content',
  standalone: true,
  templateUrl: './assigness-content.component.html',
  styleUrls: ['./assigness-content.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    DetailsSectionComponent,
    TabsInnerWrapComponent,
    QaiTableComponent,
    QaiButtonComponent,
    QaiFieldComponent,
    QaiInputComponent,
    QaiLoadingComponent,
    AddUserToAssignmentModalComponent,
    QaiIconComponent,
    QaiSlotDirective
],
})
export class AssigneesContentComponent implements OnInit {
  form!: FormGroup;
  @ViewChild('multiselect') searchInput!: QaiMultiSelectComponent;

  currentAssigneesList: IUserAssignments[] = [];
  accountUuid: string | null = null;
  moduleAssignmentUuid: string = '';
  teamUuid: string = '';
  testersList: string[] = [];

  tableData: TableRow[] = [];
  allTableData: TableRow[] = [];
  readonly headers = ASSIGNEES_TABLE_HEADERS;
  initialSortColumn: string = ASSIGNEES_TABLE_HEADERS[0];
  initialSortDirection: SortDirection = 'asc';

  isLoading = false;
  assignmentModalVisible: boolean = false;
  networkRequestInProgress: boolean = false;
  searchTerm: string = '';
  simulationsCount: number = 0;
  isCompleted: boolean = false;

  parsedTeams: IParsedTeam[] = [];

  constructor(
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private assignmentsService: AssignmentsService,
    private assignmentReactiveFormService: AssignmentReactiveFormService
  ) {}

  ngOnInit() {
    this.moduleAssignmentUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.isCompleted = this.assignmentReactiveFormService.isCompleted(this.moduleAssignmentUuid);

    this.assignmentReactiveFormService.getForm(this.moduleAssignmentUuid).subscribe((form: FormGroup) => {
      const detailsGroup = form.get('details') as FormGroup;
    
      if (detailsGroup) {
        detailsGroup.valueChanges.subscribe(detailsValue => {
          const count = detailsValue?.simulationsCount ?? 0;
          this.simulationsCount = count;
        });
    
        const initialCount = detailsGroup.value?.simulationsCount ?? 0;
        this.simulationsCount = initialCount;
      }
    });
    
    

    this.isLoading = true;
    this.getAssignments().subscribe({
      next: () => {
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  getAssignments(): Observable<IUserAssignments[]> {
    return this.assignmentsService.getUserTeams(this.moduleAssignmentUuid).pipe(
      map((res: IUserAssignments[] | undefined) => res ?? []),
      tap((res: IUserAssignments[]) => {
        this.currentAssigneesList = res;
        this.allTableData = this.transformTestingDataToTableRows(res);
        this.tableData = [...this.allTableData];
        this.cdRef.detectChanges();
      }),
      catchError((err: any) => {
        console.error(err);
        this.isLoading = false;
        return of([]);
      })
    );
  }

  handleAddUserToAssignmentClick() {
    this.assignmentModalVisible = true;
  }

  get displayedTableData(): TableRow[] {
    if (!this.searchTerm.trim()) {
      return this.allTableData;
    }

    const searchLower = this.searchTerm.toLowerCase();

    return this.allTableData.filter((row) => {
      const nameCell = row['Name'];
      const teamCell = row['Team'];
      const excludedCell = row['Excluded from Reporting'];

      const name =
        nameCell && 'value' in nameCell
          ? String(nameCell.value).toLowerCase()
          : '';
      const team =
        teamCell && 'value' in teamCell ? String(teamCell.value).toLowerCase() : '';
      const excluded =
        excludedCell && 'value' in excludedCell
          ? String(excludedCell.value).toLowerCase()
          : '';
  
      return name.includes(searchLower) || team.includes(searchLower) || excluded.includes(searchLower);
    });
  }

  searchValueChange(event: string[]) {
    console.log('search value changed:', event);
    this.testersList = event;
  }

  searchSelectionChange(event: QaiMultiSelectOption[]) {
    console.log('search selection changed:', event);
    this.testersList = event.map((opt) => opt.value);
  }

  createTestingTableRow(
    id: string,
    teamUuid: string,
    name: string,
    teamName: string,
    excludedFromReporting: boolean,
    options: KebabMenuItem[]
  ): TableRow {
    const row = {
      id,
      teamUuid,
      [ASSIGNEES_TABLE_HEADERS[0]]: { type: 'text', value: name },
      [ASSIGNEES_TABLE_HEADERS[1]]: { type: 'text', value: teamName },
      [ASSIGNEES_TABLE_HEADERS[2]]: { type: 'text', value: excludedFromReporting ? 'Yes' : '' },
    } as const;
    
    return { ...row, options } as unknown as TableRow;
  }

  onTableRowAction(event: {
    action: KebabMenuItem;
    rowId: string | number;
    rowData: TableRow;
    event: Event;
  }) {
    if (!event.action.text || event.action.text.trim() === '') return;

    if (this.isCompleted) {
      return;
    }

    const actionText = event.action.text.toLowerCase();

    switch (actionText) {
      case 'remove assignee':
        this.removeAssignment(
          event.rowData.id as string,
          event.rowData['teamUuid'] as unknown as string
        );
        break;

      case 'exclude from reporting':
        this.toggleReportingExclusion(event.rowData, true);
        break;
      case 'include in reporting':
        this.toggleReportingExclusion(event.rowData, false);
        break;

      default:
        break;
    }
  }

  private toggleReportingExclusion(rowData: TableRow, exclude: boolean): void {
    const userUuid = rowData.id as string;
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    if (!accountUuid) {
      console.error('Account UUID not found');
      return;
    }

    this.assignmentsService
      .excludeFromReporting(
        this.moduleAssignmentUuid,
        accountUuid,
        userUuid,
        exclude
      )
      .subscribe({
        next: () => {
          this.getAssignments().subscribe({});
        },
        error: (err: any) => {
          console.error('Failed to update exclude from reporting status', err);
        },
      });
  }

  removeAssignment(userUuid: string, teamUuid: string): void {
    console.log('Assignment removed');
    this.assignmentsService
      .removeAssignment(this.moduleAssignmentUuid, userUuid, teamUuid)
      .subscribe({
        next: () => {
          this.getAssignments().subscribe({});
          console.log(`Assignment removed for user: ${userUuid}`);
        },
        error: (err: any) => {
          console.error('Failed to remove assignment', err);
        },
      });
  }

  transformTestingDataToTableRows(apiData: IUserAssignments[]): TableRow[] {
    return apiData.map((item) => {
      const excludedFromReporting = item.excludeReporting ?? false;

      return this.createTestingTableRow(
        item.userUuid,
        item.teamUuid,
        item.userName,
        item.teamName,
        excludedFromReporting,
        !this.isCompleted ? [
          {
            type: 'click',
            text: 'Remove assignee',
          },
          {
            type: 'click',
            text: excludedFromReporting ? 'Include in reporting' : 'Exclude from reporting',
          },
        ] : []
      );
    });
  }

  closeAssignmentModal() {
    this.assignmentModalVisible = false;
  }

  addToAssignment() {
    this.getAssignments().subscribe({});
    this.closeAssignmentModal();
    this.networkRequestInProgress = false;
  }
}
